import { Button, Header } from '../Generic'
import { observer } from 'mobx-react'
import { useContext } from 'react'
import { StoreContext } from '@store/Root.store'
import './MediaPermissionsChecker.css'
import { actionReport } from '@methods/actionReport'
import { Illustration } from '../Illustration/Illustration'
import { getPermissionCheckerErrorIcon } from '../Illustration/helpers'

interface PermissionsErrorScreenProps {
  onRetry: () => void
}

export const PermissionsErrorScreen = observer(({ onRetry }: PermissionsErrorScreenProps) => {
  const store = useContext(StoreContext)
  const { isMobile, userAgent } = store.AppState

  const { mediaPermissionsChecker: trans } = store.TranslationsState.translations
  const { browser, system, isMobile: isMobileOS } = userAgent

  const getBrowserStepList = () => {
    if (browser === 'unknown') {
      return trans.errorDescriptionListChrome
    }

    if (system === 'android') {
      switch (browser) {
        case 'chrome':
          return trans.errorDescriptionListAndroidChrome
        case 'firefox':
          return trans.errorDescriptionListAndroidFirefox
        case 'edge':
          return trans.errorDescriptionListAndroidEdge
        case 'opera':
          return trans.errorDescriptionListAndroidOpera
        case 'samsung':
          return trans.errorDescriptionListAndroidSamsung
        default:
          return trans.errorDescriptionListAndroidChrome
      }
    }

    if (!isMobileOS) {
      switch (browser) {
        case 'chrome':
          return trans.errorDescriptionListChrome
        case 'firefox':
          return trans.errorDescriptionListFirefox
        case 'edge':
          return trans.errorDescriptionListEdge
        case 'opera':
          return trans.errorDescriptionListOpera
        case 'safari':
          return trans.errorDescriptionListSafari
        default:
          return trans.errorDescriptionListChrome
      }
    }
  }

  const renderTutorialSteps = () => {
    const supportedBrowsers = ['chrome', 'firefox', 'edge', 'opera', 'safari', 'samsung']

    if (system === 'ios' || browser === 'safari') {
      return <p className="generic__paragraph-centered">{trans.errorDescriptionIOS}</p>
    } else if (supportedBrowsers.indexOf(browser || '') === -1) {
      return <p className="generic__paragraph-centered">{trans.errorDescriptionFallback}</p>
    } else {
      return (
        <p className="generic__paragraph">
          {trans.errorDescription2}
          <br />
          <ol className="media-permissions__step-list">
            {getBrowserStepList()?.map((doctype) => (
              <li key={doctype}>{doctype}</li>
            ))}
          </ol>
        </p>
      )
    }
  }

  const renderPermissionDeniedScreen = () => {
    const permRetries = parseInt(localStorage.getItem('permRetries') || '0')
    localStorage.setItem('permRetries', (permRetries + 1).toString())

    if (permRetries < 2 && system === 'ios') {
      if (permRetries < 1) {
        onRetry()
      }

      return <></>
    } else if (permRetries < 1 && browser === 'safari') {
      onRetry()

      return <></>
    } else {
      return (
        <>
          <Illustration
            type={getPermissionCheckerErrorIcon(system ?? '', browser ?? '', isMobileOS ?? false)}
          />

          <Header>{trans.errorHeader}</Header>

          <p className="generic__paragraph-centered">{trans.errorDescription1}</p>

          {renderTutorialSteps()}

          <div className="layout__content-container-bottom-fixed">
            {!isMobile && <div className="layout__content-container-bottom-left" />}
            <Button
              className="button--primary"
              onClick={() => {
                actionReport({
                  type: 'event.onboarding-web.process.MPC_RETRY_BUTTON_CLICKED',
                  payload: {},
                })

                onRetry()
              }}
            >
              <span className="generic__button-text">{trans.retry}</span>
            </Button>
          </div>
        </>
      )
    }
  }

  return renderPermissionDeniedScreen()
})
